<template>
  <div :class="{ 'avd--empty': !entries.length, 'avd--active': editingHere || copying }" class="avd">
    <div class="avd__main">
      <span :title="dayLong" class="avd__day">{{ dayShortText }}</span>
      <div class="avd__ranges">
        <span v-if="!entries.length" class="avd__off">nicht verfügbar</span>
        <span v-for="entry in entries" :key="entry.index"
              :class="{ 'avc--invalid': !!entry.issue, 'avc--open': editingIndex === entry.index }" class="avc">
          <button :aria-label="`Zeitfenster ${rangeText(entry.item)} bearbeiten`" :disabled="disabled" class="avc__body" type="button"
                  @click="$emit('edit', editingIndex === entry.index ? -1 : entry.index)">
            <span class="avc__time">{{ rangeText(entry.item) }}</span>
            <span v-if="withLabels" class="avc__dots">
              <span v-for="label in entry.item.labels || []" :key="label" :style="{ background: labelColor(label) }" :title="labelText(label)"
                    class="avc__dot"/>
            </span>
          </button>
          <button :aria-label="`Zeitfenster ${rangeText(entry.item)} entfernen`" :disabled="disabled" class="avc__remove" title="Entfernen"
                  type="button" @click="$emit('remove', entry.index)">
            <v-icon x-small>mdi-close</v-icon>
          </button>
        </span>
        <button v-if="!disabled" class="add-link avd__add" type="button" @click="$emit('add')">
          <v-icon x-small>mdi-plus</v-icon>Zeitfenster
        </button>
      </div>
      <div :title="timelineTitle" aria-hidden="true" class="avd__timeline">
        <span v-for="tick in ticks" :key="tick" :style="{ left: `${percent(tick)}%` }" class="avd__tick"/>
        <span v-for="entry in entries" :key="`bar-${entry.index}`" :class="{ 'avd__bar--invalid': !!entry.issue }" :style="barStyle(entry.item)"
              class="avd__bar"/>
      </div>
      <button v-if="!disabled && entries.length" :class="{ 'avd__copy--on': copying }" class="avd__copy" title="Auf weitere Tage kopieren"
              type="button" @click="toggleCopy">
        <v-icon small>mdi-content-copy</v-icon>
      </button>
      <span v-else class="avd__copy avd__copy--placeholder"/>
    </div>

    <div v-for="entry in entries.filter((el) => !!el.issue)" :key="`issue-${entry.index}`" class="avd__issue">
      <v-icon x-small>mdi-alert-circle-outline</v-icon>{{ rangeText(entry.item) }}: {{ entry.issue }}
    </div>

    <v-expand-transition>
      <div v-if="editingEntry" class="avd__panel">
        <div class="d-flex align-center flex-wrap" style="gap: 8px">
          <label :class="{ 'time-chip--invalid': editingEntry.issue }" class="time-chip">
            <span class="acq-tiny">Von</span>
            <input :value="toTime(editingEntry.item.timeRange.startTime)" aria-label="Von" step="300" type="time"
                   @input="setTime('startTime', $event.target.value)"/>
          </label>
          <label :class="{ 'time-chip--invalid': editingEntry.issue }" class="time-chip">
            <span class="acq-tiny">Bis</span>
            <input :value="toTime(editingEntry.item.timeRange.endTime)" aria-label="Bis" step="300" type="time"
                   @input="setTime('endTime', $event.target.value)"/>
          </label>
          <span class="acq-tiny">{{ durationText(editingEntry.item) }}</span>
        </div>
        <div v-if="withLabels" class="d-flex flex-column" style="gap: 6px">
          <span class="acq-label">Markierung</span>
          <div class="pills" role="group" aria-label="Markierung">
            <button v-for="label in labels" :key="label" :aria-pressed="String(hasLabel(label))" :class="{ 'pill--active': hasLabel(label) }"
                    class="pill avd__pill" type="button" @click="toggleLabel(label)">
              <span :style="{ background: labelColor(label) }" class="avc__dot"/>{{ labelText(label) }}
            </button>
          </div>
        </div>
        <div class="d-flex align-center" style="gap: 8px">
          <button class="cta cta--danger avd__btn" type="button" @click="$emit('remove', editingEntry.index)">
            <v-icon class="cta__icon" small>mdi-delete-outline</v-icon>Entfernen
          </button>
          <v-spacer/>
          <button class="cta cta--quiet avd__btn" type="button" @click="$emit('edit', -1)">
            <v-icon class="cta__icon" small>mdi-check</v-icon>Fertig
          </button>
        </div>
      </div>
    </v-expand-transition>

    <v-expand-transition>
      <div v-if="copying" class="avd__panel">
        <span class="acq-label">{{ dayLong }} übernehmen für …</span>
        <div class="pills" role="group" aria-label="Zieltage">
          <button v-for="target in copyTargetsAvailable" :key="target" :aria-pressed="String(copyTargets.includes(target))"
                  :class="{ 'pill--active': copyTargets.includes(target) }" class="pill avd__pill" type="button" @click="toggleTarget(target)">
            {{ dayName(target) }}
          </button>
          <button class="add-link" type="button" @click="copyTargets = workdayTargets">Alle Werktage</button>
        </div>
        <div class="d-flex align-center" style="gap: 8px">
          <span class="acq-tiny flex-grow-1">Vorhandene Zeitfenster der Zieltage werden ersetzt.</span>
          <button class="cta cta--ghost avd__btn" type="button" @click="copying = false">Abbrechen</button>
          <button :disabled="!copyTargets.length" class="cta cta--quiet avd__btn" type="button" @click="applyCopy">
            <v-icon class="cta__icon" small>mdi-content-copy</v-icon>Kopieren
          </button>
        </div>
      </div>
    </v-expand-transition>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {
  ALL_DAYS, DAY_END, DAY_START, EditableAvailability, WORKDAYS, dayShort, dayText, labelColor, labelText, minutesToTime, timeToMinutes,
} from './availabilityEditor.helper';

export interface DayEntry {
  index: number;
  item: EditableAvailability;
  issue?: string;
}

/** One weekday of the availability week editor: range chips, inline editor of the open chip, mini timeline and copy action */
export default Vue.extend({
  props: {
    day: {type: Number, required: true},
    entries: {type: Array as () => DayEntry[], default: () => []},
    editingIndex: {type: Number, default: -1},
    /** Allowed label values; empty = entity without labels */
    labels: {type: Array as () => string[], default: () => []},
    disabled: {type: Boolean, default: false},
    /** Days shown in the copy picker */
    days: {type: Array as () => number[], default: () => ALL_DAYS},
  },
  data: () => ({
    copying: false,
    copyTargets: [] as number[],
  }),
  computed: {
    ticks: () => [8 * 60, 10 * 60, 12 * 60, 14 * 60, 16 * 60, 18 * 60],
    withLabels(): boolean {
      return this.labels.length > 0;
    },
    dayShortText(): string {
      return dayShort(this.day);
    },
    dayLong(): string {
      return dayText(this.day);
    },
    editingEntry(): DayEntry | undefined {
      return this.entries.find((entry) => entry.index === this.editingIndex);
    },
    editingHere(): boolean {
      return !!this.editingEntry;
    },
    copyTargetsAvailable(): number[] {
      return this.days.filter((day) => day !== this.day);
    },
    workdayTargets(): number[] {
      return WORKDAYS.filter((day) => day !== this.day);
    },
    timelineTitle(): string {
      return this.entries.length ? this.entries.map((entry) => this.rangeText(entry.item)).join(', ') : 'Keine Zeitfenster';
    },
  },
  methods: {
    labelColor,
    labelText,
    toTime: minutesToTime,
    dayName(day: number): string {
      return dayShort(day);
    },
    rangeText(item: EditableAvailability): string {
      return `${minutesToTime(item.timeRange.startTime)}–${minutesToTime(item.timeRange.endTime)}`;
    },
    durationText(item: EditableAvailability): string {
      const minutes = item.timeRange.endTime - item.timeRange.startTime;
      if (minutes <= 0) {
        return '';
      }
      const hours = Math.floor(minutes / 60);
      const rest = minutes % 60;
      return `${hours ? `${hours} Std.` : ''}${hours && rest ? ' ' : ''}${rest ? `${rest} Min.` : ''}`;
    },
    percent(minutes: number): number {
      const clamped = Math.max(DAY_START, Math.min(minutes, DAY_END));
      return ((clamped - DAY_START) / (DAY_END - DAY_START)) * 100;
    },
    barStyle(item: EditableAvailability): Record<string, string> {
      const left = this.percent(item.timeRange.startTime);
      const width = Math.max(this.percent(item.timeRange.endTime) - left, 0);
      const first = (item.labels || [])[0];
      return {left: `${left}%`, width: `${width}%`, background: first ? labelColor(first) : '#1E88E5'};
    },
    hasLabel(label: string): boolean {
      return !!this.editingEntry && (this.editingEntry.item.labels || []).includes(label);
    },
    emitUpdate(item: EditableAvailability) {
      if (this.editingEntry) {
        this.$emit('update', {index: this.editingEntry.index, item});
      }
    },
    setTime(key: 'startTime' | 'endTime', value: string) {
      const minutes = timeToMinutes(value);
      if (!this.editingEntry || minutes === undefined) {
        return;
      }
      const item = this.editingEntry.item;
      this.emitUpdate({...item, timeRange: {...item.timeRange, [key]: minutes}});
    },
    toggleLabel(label: string) {
      if (!this.editingEntry) {
        return;
      }
      const item = this.editingEntry.item;
      const current = item.labels || [];
      const labels = current.includes(label) ? current.filter((el) => el !== label) : [...current, label];
      this.emitUpdate({...item, labels});
    },
    toggleCopy() {
      this.copying = !this.copying;
      this.copyTargets = [];
      if (this.copying) {
        this.$emit('edit', -1);
      }
    },
    toggleTarget(day: number) {
      this.copyTargets = this.copyTargets.includes(day) ? this.copyTargets.filter((el) => el !== day) : [...this.copyTargets, day];
    },
    applyCopy() {
      this.$emit('copy', [...this.copyTargets]);
      this.copying = false;
      this.copyTargets = [];
    },
  },
  watch: {
    editingIndex(index: number) {
      if (index >= 0 && this.editingHere) {
        this.copying = false;
      }
    },
  },
});
</script>

<style scoped>
.avd {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 12px;
  transition: background .15s ease;
}

.avd--active {
  background: rgba(15, 23, 42, .03);
}

.avd__main {
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr) 160px 28px;
  align-items: center;
  gap: 10px;
}

.avd__day {
  font-size: 13px;
  font-weight: 600;
  color: rgba(15, 23, 42, .8);
}

.avd--empty .avd__day {
  color: rgba(15, 23, 42, .4);
}

.avd__ranges {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.avd__off {
  font-size: 12px;
  color: rgba(15, 23, 42, .4);
}

.avd__add {
  font-size: 12px;
  margin-left: 2px;
}

.avc {
  display: inline-flex;
  align-items: center;
  height: 28px;
  border-radius: 14px;
  background: #E8F1FD;
  color: #0D47A1;
  box-shadow: inset 0 0 0 1.5px transparent;
  transition: box-shadow .15s ease, background .15s ease;
}

.avc--open {
  box-shadow: inset 0 0 0 1.5px #1976D2;
}

.avc--invalid {
  background: #FDECEC;
  color: #B71C1C;
  box-shadow: inset 0 0 0 1.5px #E5484D;
}

.avc__body {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 100%;
  padding: 0 4px 0 10px;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.avc__body:focus-visible, .avc__remove:focus-visible, .avd__copy:focus-visible {
  outline: 2px solid #1976D2;
  outline-offset: 1px;
  border-radius: 12px;
}

.avc__time {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.avc__dots {
  display: inline-flex;
  gap: 3px;
}

.avc__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  display: inline-block;
  flex: none;
}

.avc__remove {
  display: inline-grid;
  place-items: center;
  width: 22px;
  height: 22px;
  margin-right: 3px;
  border: 0;
  border-radius: 50%;
  background: none;
  color: inherit;
  cursor: pointer;
  opacity: .6;
}

.avc__remove:hover {
  opacity: 1;
  background: rgba(15, 23, 42, .08);
}

.avc__remove .v-icon {
  color: inherit !important;
}

.avd__timeline {
  position: relative;
  height: 10px;
  border-radius: 5px;
  background: rgba(15, 23, 42, .06);
  overflow: hidden;
}

.avd__tick {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(15, 23, 42, .08);
}

.avd__bar {
  position: absolute;
  top: 1px;
  bottom: 1px;
  border-radius: 4px;
  opacity: .85;
}

.avd__bar--invalid {
  background: #E5484D !important;
}

.avd__copy {
  display: inline-grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 8px;
  background: none;
  color: rgba(15, 23, 42, .45);
  cursor: pointer;
}

.avd__copy:hover, .avd__copy--on {
  background: rgba(15, 23, 42, .06);
  color: #1565C0;
}

.avd__copy .v-icon {
  color: inherit !important;
}

.avd__copy--placeholder {
  cursor: default;
}

.avd__issue {
  display: flex;
  align-items: center;
  gap: 4px;
  padding-left: 42px;
  font-size: 12px;
  color: #C62828;
}

.avd__issue .v-icon {
  color: inherit !important;
}

.avd__panel {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-left: 42px;
  padding: 12px;
  border-radius: 12px;
  background: #FFFFFF;
  box-shadow: 0 1px 2px rgba(15, 23, 42, .06), 0 4px 14px rgba(15, 23, 42, .06);
}

.avd__pill {
  height: 28px;
  font-size: 12px;
}

.avd__btn {
  height: 30px;
}

.time-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 10px;
  border-radius: 10px;
  background: #E8F1FD;
  color: #0D47A1;
  font-size: 13px;
}

.time-chip--invalid {
  background: #FDECEC;
  color: #B71C1C;
  box-shadow: inset 0 0 0 1.5px #E5484D;
}

.time-chip input {
  border: 0;
  outline: none;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: 600;
}

/* Phones (office dialog fullscreen, Lehrkraft-Bereich › Zeiten ändern): larger touch targets */
@media (max-width: 599px) {
  .avd__main {
    grid-template-columns: 28px minmax(0, 1fr) 36px;
  }

  .avc {
    height: 36px;
    border-radius: 18px;
  }

  .avc__body {
    font-size: 13px;
  }

  .avc__remove {
    width: 30px;
    height: 30px;
  }

  .avd__copy {
    width: 36px;
    height: 36px;
  }

  .avd__add {
    min-height: 36px;
  }

  .avd__pill, .avd__btn {
    height: 36px;
  }

  .time-chip {
    height: 40px;
  }

  .avd__timeline {
    grid-column: 2 / 3;
    grid-row: 2;
  }

  .avd__panel {
    margin-left: 0;
  }

  .avd__issue {
    padding-left: 38px;
  }
}

.theme--dark .avd__day {
  color: rgba(255, 255, 255, .85);
}

.theme--dark .avd--empty .avd__day, .theme--dark .avd__off {
  color: rgba(255, 255, 255, .4);
}

.theme--dark .avd--active {
  background: rgba(255, 255, 255, .04);
}

.theme--dark .avc, .theme--dark .time-chip {
  background: rgba(100, 181, 246, .16);
  color: #90CAF9;
}

.theme--dark .avc--invalid, .theme--dark .time-chip--invalid {
  background: rgba(239, 83, 80, .16);
  color: #EF9A9A;
}

.theme--dark .avd__timeline {
  background: rgba(255, 255, 255, .08);
}

.theme--dark .avd__panel {
  background: #2A2A2A;
}

.theme--dark .avd__copy {
  color: rgba(255, 255, 255, .5);
}
</style>
